import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Filter } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { PROJECT_TYPES, PROJECT_STAGES, PROFILE_TYPES } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";
import VerifiedBadge from "@/components/VerifiedBadge";

const selCls = "border border-tertiary bg-white px-3 py-2.5 text-sm outline-none focus:border-champagne";

export default function ProjectsList() {
  const { lang, L } = useLang();
  useSeo({ title: lang === "fr" ? "Projets" : "Projects",
    description: lang === "fr" ? "Projets qualifiés en recherche de financement, d'investisseurs, de partenaires ou d'associés." : "Qualified projects seeking financing, investors, partners or associates.", lang });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tax, setTax] = useState({ sector: [], market: [] });
  const [f, setF] = useState({ q: "", project_type: "", sector: "", country: "", profile: "", verification: "" });

  useEffect(() => {
    Promise.all([api.get("/taxonomies", { params: { type: "sector" } }), api.get("/taxonomies", { params: { type: "market" } })])
      .then(([s, m]) => setTax({ sector: s.data, market: m.data })).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/projects", { params }).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, [f]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <section className="border-b border-tertiary bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-16">
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">RSTI</span>
          <h1 className="mt-3 font-serif text-4xl tracking-tight text-anthracite sm:text-5xl">{lang === "fr" ? "Projets" : "Projects"}</h1>
          <p className="mt-3 max-w-2xl text-anthracite/70">{lang === "fr" ? "Projets qualifiés par RSTI : recherche de financement, d'investisseurs, de partenaires, d'associés et de fournisseurs." : "Projects qualified by RSTI: seeking financing, investors, partners, associates and suppliers."}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="mb-8 border border-tertiary bg-white p-4">
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-anthracite/40" />
            <input data-testid="proj-search" value={f.q} onChange={(e) => set("q", e.target.value)} placeholder={lang === "fr" ? "Rechercher un projet…" : "Search a project…"} className="w-full border border-tertiary bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-champagne" />
          </div>
          <div className="flex flex-wrap gap-2">
            <select data-testid="filter-proj-type" value={f.project_type} onChange={(e) => set("project_type", e.target.value)} className={selCls}>
              <option value="">{lang === "fr" ? "Tous les types" : "All types"}</option>
              {PROJECT_TYPES.map((o) => <option key={o.value} value={o.value}>{o[lang]}</option>)}
            </select>
            <select value={f.sector} onChange={(e) => set("sector", e.target.value)} className={selCls}>
              <option value="">{lang === "fr" ? "Tous secteurs" : "All sectors"}</option>
              {tax.sector.map((s) => <option key={s.slug} value={s.slug}>{s[`label_${lang}`]}</option>)}
            </select>
            <select value={f.country} onChange={(e) => set("country", e.target.value)} className={selCls}>
              <option value="">{lang === "fr" ? "Tous marchés" : "All markets"}</option>
              {tax.market.map((s) => <option key={s.slug} value={s.slug}>{s[`label_${lang}`]}</option>)}
            </select>
            <select value={f.profile} onChange={(e) => set("profile", e.target.value)} className={selCls}>
              <option value="">{lang === "fr" ? "Profil recherché" : "Profile sought"}</option>
              {PROFILE_TYPES.map((p) => <option key={p.value} value={p.value}>{p[lang]}</option>)}
            </select>
          </div>
        </div>

        {loading ? <p className="py-16 text-center text-anthracite/40">…</p> : items.length === 0 ? (
          <div className="border border-dashed border-tertiary bg-white py-20 text-center" data-testid="proj-empty">
            <Filter className="mx-auto mb-3 h-6 w-6 text-anthracite/30" />
            <p className="text-anthracite/50">{lang === "fr" ? "Aucun projet publié pour ces critères." : "No published project for these criteria."}</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((o) => (
              <Link key={o.id} to={`/projets/${o.slug}`} data-testid={`proj-card-${o.slug}`} className="group flex flex-col border border-tertiary bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                {o.cover_image ? <div className="aspect-[16/10] overflow-hidden bg-sand"><img src={o.cover_image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /></div> : <div className="aspect-[16/10] bg-gradient-to-br from-sand to-tertiary" />}
                <div className="flex flex-1 flex-col p-6">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-champagne">{PROJECT_TYPES.find((x) => x.value === o.project_type)?.[lang]}</span>
                    <VerifiedBadge verified={o.rst_verified} />
                  </div>
                  <h3 className="mb-2 font-serif text-xl leading-snug text-anthracite">{L(o, "title")}</h3>
                  <p className="mb-4 flex-1 text-sm leading-relaxed text-anthracite/70 line-clamp-2">{L(o, "summary") || L(o, "need")}</p>
                  {o.amount_text && <p className="mb-2 text-sm font-medium text-anthracite">{o.amount_text}</p>}
                  <div className="flex items-center justify-between border-t border-tertiary pt-4 text-xs text-anthracite/50">
                    <span className="flex items-center gap-1.5">{(o.sector || o.country) && <MapPin className="h-3.5 w-3.5" />}{[o.sector, o.country].filter(Boolean).join(" · ")}</span>
                    <span>{PROJECT_STAGES.find((s) => s.value === o.stage)?.[lang]}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
